import { Injectable } from '@angular/core';
import { ToastService } from '../../../services/toast.service';
import { NARRATIVE_CONSTANTS } from '../constants/narrative.constants';
import { WorkflowError } from '../models/narrative.model';
import { NarrativeHelper } from '../utils/narrative-helper';
import { I18nService } from '../../../i18n/i18n.service';

export interface ErrorContext {
  type: 'image-generation' | 'video-generation' | 'file-upload' | 'combination' | 'validation';
  sceneId?: string;
  sceneIndex?: number;
  operation?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NarrativeErrorHandlerService {
  private errors: WorkflowError[] = [];

  constructor(private toastService: ToastService, private i18n: I18nService) {}

  /**
   * エラーを処理し、適切なメッセージを表示
   */
  handleError(error: any, context: ErrorContext): WorkflowError {
    const errorMessage = this.extractErrorMessage(error);
    const contextualMessage = this.createContextualMessage(errorMessage, context);
    
    const workflowError: WorkflowError = {
      id: NarrativeHelper.generateId(),
      type: context.type,
      sceneId: context.sceneId,
      message: contextualMessage,
      timestamp: new Date()
    };

    this.errors.push(workflowError);
    this.logError(error, context);
    this.showErrorToast(contextualMessage);

    return workflowError;
  }

  /**
   * 検証エラーを処理
   */
  handleValidationError(message: string): void {
    this.toastService.showToast(message, 'error');
  }

  /**
   * 成功メッセージを表示
   */
  showSuccess(message: string): void {
    this.toastService.showToast(message, 'success');
  }

  /**
   * 情報メッセージを表示
   */
  showInfo(message: string): void {
    this.toastService.showToast(message, 'info');
  }

  /**
   * エラーメッセージを抽出
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    if (error?.error?.message) {
      return error.error.message;
    }
    
    if (error?.statusText) {
      return error.statusText;
    }
    
    return this.i18n.t('toast.unknownErrorOccurred');
  }

  /**
   * コンテキストに応じたエラーメッセージを作成
   */
  private createContextualMessage(errorMessage: string, context: ErrorContext): string {
    switch (context.type) {
      case 'image-generation':
        return context.sceneIndex !== undefined
          ? this.i18n.t('toast.sceneImageGenError', { n: String(context.sceneIndex + 1), detail: errorMessage })
          : this.i18n.t('toast.imageGenError', { detail: errorMessage });
      case 'video-generation':
        return context.sceneIndex !== undefined
          ? this.i18n.t('toast.sceneVideoGenError', { n: String(context.sceneIndex + 1), detail: errorMessage })
          : this.i18n.t('toast.videoGenError', { detail: errorMessage });
      case 'file-upload':
        return this.i18n.t('toast.fileUploadError', { detail: errorMessage });
      case 'combination':
        return this.i18n.t('toast.videoCombineError', { detail: errorMessage });
      case 'validation':
        return this.i18n.t('toast.validationErrorPrefix', { detail: errorMessage });
      default:
        return errorMessage;
    }
  }

  /**
   * エラーをコンソールにログ出力
   */
  private logError(error: any, context: ErrorContext): void {
    console.error(`[NarrativeErrorHandler] ${context.type}`, {
      error,
      context,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * エラートーストを表示
   */
  private showErrorToast(message: string): void {
    this.toastService.showToast(message, 'error');
  }

  /**
   * エラー履歴を取得
   */
  getErrors(): WorkflowError[] {
    return [...this.errors];
  }

  /**
   * 特定のタイプのエラーを取得
   */
  getErrorsByType(type: WorkflowError['type']): WorkflowError[] {
    return this.errors.filter(error => error.type === type);
  }

  /**
   * エラー履歴をクリア
   */
  clearErrors(): void {
    this.errors = [];
  }

  /**
   * 最新のエラーを取得
   */
  getLatestError(): WorkflowError | undefined {
    return this.errors[this.errors.length - 1];
  }
}